import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F5F8FF]">
      {/* Header */}
      <header className="border-b border-[#E8EDF5] bg-[#F5F8FF]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-[#1B5AFF]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-[#0A0F1E] mb-2">Privacy Policy</h1>
          <p className="text-[#5B6472] mb-8">Last Updated: March 6, 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-[#0A0F1E]">
            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Who We Are</h2>
              <p>
                VETT is a verified work record platform operated at vettpro.org. Our mission is to create a trusted ecosystem where employment history is verified, transparent, and owned by the professionals who built it.
              </p>
              <p>
                Contact us at <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">What Data We Collect</h2>
              <p>We collect the following categories of data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Data:</strong> Name, email address, password hash, account type (employer or employee).</li>
                <li><strong>Profile Data:</strong> Profile photo, job title, industry, location, bio, skills, LinkedIn URL, education, and work preferences.</li>
                <li><strong>Employment Record Data:</strong> Work history, job titles, employment dates, performance notes, ratings, strengths, growth areas, flags, and performance reviews.</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, session duration, and interactions with the platform.</li>
                <li><strong>Payment Data:</strong> Processed by Stripe. We never store your card details directly.</li>
                <li><strong>Communication Data:</strong> Messages sent through the platform between employers and employees.</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device type, operating system, and cookies.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Why We Collect It</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and operate the VETT platform</li>
                <li>To enable employers to create verified work records</li>
                <li>To match employers with qualified candidates</li>
                <li>To send transactional notifications (record submissions, messages, etc.)</li>
                <li>To process payments for subscription plans</li>
                <li>To improve the platform through analytics (with your consent)</li>
                <li>To serve relevant marketing (with your consent)</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Who We Share It With</h2>
              <p>We share your data with the following third-party service providers who help us operate VETT:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>MongoDB Atlas:</strong> Database infrastructure. <a href="https://www.mongodb.com/legal/privacy-policy" className="text-[#1B5AFF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                <li><strong>Stripe:</strong> Payment processing. <a href="https://stripe.com/privacy" className="text-[#1B5AFF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                <li><strong>Resend:</strong> Transactional email delivery. <a href="https://resend.com/legal/privacy-policy" className="text-[#1B5AFF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                <li><strong>Google Analytics:</strong> Usage analytics (with consent). <a href="https://policies.google.com/privacy" className="text-[#1B5AFF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                <li><strong>Meta:</strong> Marketing pixel (with consent). <a href="https://www.facebook.com/privacy/policy" className="text-[#1B5AFF] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              </ul>
              <p className="mt-4 font-semibold">We do not sell personal data to any third party. Ever.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Data Retention</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account data:</strong> Retained for the duration of your account plus 30 days after deletion.</li>
                <li><strong>VETT Records:</strong> Retained even after employer account deletion because the data belongs to the employee. Employees can request deletion of their own records at any time.</li>
                <li><strong>Payment records:</strong> Retained for 7 years for legal and accounting compliance.</li>
                <li><strong>Technical logs:</strong> Retained for 90 days for security and debugging purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Your Rights</h2>
              <p>You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of all data we hold about you.</li>
                <li><strong>Right to Correction:</strong> Request correction of inaccurate data.</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your account and associated data.</li>
                <li><strong>Right to Data Portability:</strong> Export your data in JSON format upon request.</li>
                <li><strong>Right to Object:</strong> Object to processing of your data for marketing purposes.</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw cookie consent at any time via the cookie settings.</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, email us at <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>. We will respond within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Cookies</h2>
              <p>
                VETT uses cookies to operate the platform, understand usage patterns, and (with your consent) serve relevant marketing. We use four categories of cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the platform to function.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with VETT.</li>
                <li><strong>Marketing Cookies:</strong> Used to show relevant ads and measure campaigns.</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and personalize your experience.</li>
              </ul>
              <p className="mt-4">
                For full details, see our <Link to="/cookies" className="text-[#1B5AFF] hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Children's Privacy</h2>
              <p>
                VETT is not intended for users under the age of 18. We do not knowingly collect personal data from minors. If we become aware that we have collected data from a person under 18, we will delete it immediately. If you believe a minor has provided us with personal data, please contact us at <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Security</h2>
              <p>
                We implement industry-standard security measures to protect your data, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption in transit (HTTPS/TLS)</li>
                <li>Encryption at rest for stored data</li>
                <li>Row-level database security</li>
                <li>Regular security audits</li>
                <li>Secure password hashing</li>
                <li>Rate limiting on authentication endpoints</li>
              </ul>
              <p className="mt-4">
                However, no system is 100% secure. We cannot guarantee absolute security of your data. Please use a strong, unique password for your VETT account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify users by email at least 30 days before any material changes take effect. The "Last Updated" date at the top of this page indicates when this policy was last revised.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Contact</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="mt-2">
                <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8EDF5] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1B5AFF]" />
              <span className="text-[#5B6472]">© 2026 VETT. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-white">Privacy Policy</Link>
              <Link to="/terms" className="text-[#5B6472] hover:text-white">Terms of Service</Link>
              <Link to="/cookies" className="text-[#5B6472] hover:text-white">Cookie Policy</Link>
              <Link to="/impressum" className="text-[#5B6472] hover:text-white">Impressum</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
